import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';
import config from 'config';
import routes from 'utils/routes';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { selectors as settingsSelectors } from 'state/settings';
import { selectors as authSelectors } from 'state/auth';
import { actions as uiActions } from 'state/ui';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
// styles
import styled, { css } from 'styled-components';
import { media } from 'utils/style';
// components
import { Link } from 'react-router-dom';
import { ResponsiveHide } from 'refactored/ui/utils';
import useClickOutside from 'refactored/ui/utils/useClickOutside';
import { IS_TENANT, SERVICES } from 'tenant/utils';
import { selectors as clientSelectors, constants } from 'state/clientConfig';

const ExternalLink = ({ children, href, onClick }) => (
  <a href={href} target="_blank" rel="noreferrer" onClick={onClick}>
    {children}
  </a>
);

const InternalLink = ({ children, href, onClick }) => (
  <Link to={href} onClick={onClick}>
    {children}
  </Link>
);

const MenuEntry = ({ children, role = 'menuitem' }) => (
  <li role={role}>{children}</li>
);

const OfferMenuEntry = ({ onEntryClick }) => {
  const { t } = useTranslation(['mainMenu']);
  const dispatch = useDispatch();

  const country = useSelector(settingsSelectors.getCountrySettings)?.country;

  // data for analytics event
  const user = useSelector(authSelectors.getUser);
  const shipper = useSelector(authSelectors.getShipper);

  const intraserCoverageLink = 'https://www.grupotrimodos.com/en/intraser';

  const onClick = () => {
    onEntryClick();
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.openOfferPdf({
          user,
          shipper,
        })
      )
    );
  };

  return (
    <MenuEntry>
      {!IS_TENANT && (
        <ExternalLink onClick={onClick} href={config.OFFER_LINKS[country]}>
          {t('offer')}
        </ExternalLink>
      )}
      {IS_TENANT && (
        <ExternalLink onClick={onClick} href={intraserCoverageLink}>
          Sobre nosotros
        </ExternalLink>
      )}
    </MenuEntry>
  );
};

const HelpMenuEntry = ({ onEntryClick }) => {
  const { t } = useTranslation(['mainMenu']);
  const dispatch = useDispatch();

  const onClick = useCallback(() => {
    onEntryClick();
    dispatch(analyticsActions.sendEvent(analyticsEvents.openFaqsModal()));
    dispatch(uiActions.toggleModalFaqs());
  }, [onEntryClick, dispatch]);

  return (
    <MenuEntry>
      <ExternalLink onClick={onClick}>{t('help')}</ExternalLink>
    </MenuEntry>
  );
};

const SecondaryMenuEntries = ({ onEntryClick, onClickOutside, className }) => {
  const isXpo = useSelector((state) =>
    clientSelectors.getSpecificClientConfig(state, constants.IS_XPO)
  );

  const clickRef = React.useRef();
  useClickOutside(clickRef, () => {
    onClickOutside();
  });
  const { t } = useTranslation(['mainMenu', 'Profile']);
  return (
    <ul className={className} ref={clickRef}>
      {isXpo && (
        <MenuEntry>
          <InternalLink onClick={onEntryClick} href={routes.profile}>
            {t('Profile:user_profile_title')}
          </InternalLink>
        </MenuEntry>
      )}
      {!isXpo && (
        <>
          {/* settings */}
          <MenuEntry>
            <InternalLink onClick={onEntryClick} href={routes.settings}>
              {t('settings')}
            </InternalLink>
          </MenuEntry>
          {!IS_TENANT && (
            <>
              {/* organization members */}
              <MenuEntry>
                <InternalLink
                  onClick={onEntryClick}
                  href={routes.organizationMembers}
                >
                  {t('organization_members')}
                </InternalLink>
              </MenuEntry>
              {/* addresses (only mobile, is on main menu) */}
              <ResponsiveHide hideNormal>
                <MenuEntry>
                  <InternalLink onClick={onEntryClick} href={routes.addresses}>
                    {t('addresses')}
                  </InternalLink>
                </MenuEntry>
              </ResponsiveHide>
            </>
          )}

          {/* billing (only mobile, is on main menu) */}
          <ResponsiveHide hideNormal>
            <MenuEntry role="menuitem">
              <InternalLink onClick={onEntryClick} href={routes.billing}>
                {t('billing')}
              </InternalLink>
            </MenuEntry>
          </ResponsiveHide>
          {/* analytics */}
          <ResponsiveHide hideNormal>
            {SERVICES.SUPERSET && (
              <MenuEntry>
                <InternalLink onClick={onEntryClick} href={routes.dashboard}>
                  {t('analytics')}
                </InternalLink>
              </MenuEntry>
            )}
          </ResponsiveHide>
          {/* separator */}
          <MenuEntry role="presentation">
            <hr />
          </MenuEntry>
          {/* offer */}
          <OfferMenuEntry onEntryClick={onEntryClick} />
          {/* help */}
          <HelpMenuEntry onEntryClick={onEntryClick} />
          {/* separator */}
          <MenuEntry role="presentation">
            <hr />
          </MenuEntry>
        </>
      )}
      {/* logout */}
      <MenuEntry>
        <InternalLink onClick={onEntryClick} href={routes.logout}>
          {t('logout')}
        </InternalLink>
      </MenuEntry>
    </ul>
  );
};

SecondaryMenuEntries.propTypes = {
  onEntryClick: PropTypes.func,
  onClickOutside: PropTypes.func,
  className: PropTypes.string,
};

const regularCSS = css`
  margin: 0;
  padding: 0;
  list-style: none;
  background-color: var(--white);
  li {
    a {
      display: block;
      width: 288px;
      padding: 8px 24px;
      font-size: 16px;
      line-height: 24px;
      text-decoration: none;
      color: var(--grey900);
      cursor: pointer;
      svg {
        height: 16px;
        width: 16px;
        margin-right: 5px;
      }
      &:hover {
        background-color: var(--primary100);
      }
    }
    hr {
      margin: 8px 24px;
    }
  }
  & > li:last-child a {
    color: var(--primary500);
  }
`;

const mobileCSS = css`
  li {
    a {
      width: 100%;
    }
  }
`;

const StyledSecondaryMenuEntries = styled(SecondaryMenuEntries)`
  ${regularCSS}
  ${media.lessThan('small')`${mobileCSS}`}
`;

export default StyledSecondaryMenuEntries;
